import { useEffect } from "react";

import { WithStringValues } from "common/utils/types";

import Results from "client/common/components/results/Results";
import Body from "client/common/components/layouts/Body";
import { ProductListSource } from "client/analytics/types";

import { useProductSearchQuery } from "../hooks";
import { ProductSearchSearch } from "../types";
import ProductCardResults from "./ProductCardResults";
import { getProductFiltersFromSearch, getProductListSort, toProductListSortParam } from "../utils";

import styles from "../styles/ProductSearch.module.less";

type ProductSearchProps = {
  search: WithStringValues<ProductSearchSearch>;
  onUpdateSearch: (search: Partial<ProductSearchSearch>) => void;
};

const ProductSearch = ({ search, onUpdateSearch }: ProductSearchProps) => {
  const {
    data: products,
    isLoading,
    error,
    isRefetching,
    isPlaceholderData,
    isFetchedAfterMount,
  } = useProductSearchQuery(search);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [search.page]);

  if (isLoading) {
    return <Results.Loading description="Loading products" />;
  }

  if (error || !products) {
    return <Results.FetchError error={error} entityName="Product" />;
  }

  const sort = getProductListSort(search.sort);

  return (
    <Body>
      <ProductCardResults
        products={products}
        isRefetching={isRefetching}
        isStale={isPlaceholderData}
        filters={getProductFiltersFromSearch(search)}
        onFiltersChange={onUpdateSearch}
        onPageChange={(page) => onUpdateSearch({ page })}
        listSource={ProductListSource.SearchResults}
        listContext={{
          searchTerm: search.q,
          latencyMs: isFetchedAfterMount ? products.latencyMs : undefined,
        }}
        entityName="result"
        empty={{ description: `Could not find any results for "${search.q}"` }}
        className={styles.container}
        sort={sort}
        onSortChange={(next) => onUpdateSearch({ sort: toProductListSortParam(next) })}
      />
    </Body>
  );
};

export default ProductSearch;
